import type { Booking, Facility, Inspection, MaintenanceRecord, MaintenanceRequest } from "../../services/facilityService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Facilities: the school's rooms and spaces, who has booked them, repairs
 * asked for and done, and inspections. What can be done with a record here
 * mirrors apps/facility/views.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const time = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const ugx = (value?: string | number | null) => `UGX ${Number(value || 0).toLocaleString()}`;
export const weekday = (value?: string | null) => {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
export const addDays = (iso: string, days: number) => {
  const [y, m, d] = iso.split("-").map(Number);
  const next = new Date(y, m - 1, d + days);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
};
/** The Monday of the week a day falls in. */
export const mondayOf = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  const weekdayIndex = (new Date(y, m - 1, d).getDay() + 6) % 7;
  return addDays(iso, -weekdayIndex);
};

type Pairs = [string, string][];
export const FACILITY_TYPES: Pairs = [["classroom", "Classroom"], ["laboratory", "Laboratory"], ["library", "Library"], ["hall", "Hall"], ["office", "Office"], ["sports", "Sports ground or court"], ["cafeteria", "Dining hall"], ["hostel", "Dormitory"], ["playground", "Playground"], ["garden", "Garden"], ["parking", "Parking"], ["other", "Other"]];
export const PRIORITIES: Pairs = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
export const CONDITIONS: Pairs = [["excellent", "Excellent"], ["good", "Good"], ["fair", "Fair"], ["poor", "Poor"], ["unsafe", "Unsafe"]];
export const INSPECTION_TYPES: Pairs = [["routine", "Routine"], ["safety", "Safety"], ["fire", "Fire safety"], ["electrical", "Electrical"], ["plumbing", "Plumbing"], ["structural", "Structural"], ["other", "Other"]];
export const RECORD_TYPES: Pairs = [["preventive", "Routine service"], ["corrective", "Repair"], ["emergency", "Emergency repair"], ["upgrade", "Upgrade"], ["inspection", "Inspection work"]];
export const labelOf = (pairs: Pairs, value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value || "—");

export const PRIORITY_TONE: Record<string, string> = { low: "text-gray-600", medium: "text-gray-900", high: "font-semibold text-amber-800", urgent: "font-semibold text-red-700" };
export const CONDITION_TONE: Record<string, string> = { excellent: "text-emerald-700", good: "text-emerald-700", fair: "text-gray-900", poor: "font-semibold text-amber-800", unsafe: "font-semibold text-red-700" };

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

export const location = (row: Pick<Facility, "building" | "floor" | "room_number">) =>
  [row.building, row.floor && `floor ${row.floor}`, row.room_number && `room ${row.room_number}`].filter(Boolean).join(", ");

// ---- Facilities ------------------------------------------------------------------------

export function facilityStatus(row: Pick<Facility, "is_active" | "is_available" | "last_condition">): Tone {
  if (!row.is_active) return tone("Out of use", "bg-gray-400");
  if (row.last_condition === "unsafe") return tone("Unsafe", "bg-red-500");
  if (!row.is_available) return tone("Closed for now", "bg-amber-500");
  return tone("Open", "bg-emerald-500");
}

const minutes = (value: string) => { const [h, m] = value.split(":").map(Number); return h * 60 + (m || 0); };
/** Days a booking covers inside a window, inclusive. */
function daysInside(row: Pick<Booking, "start_date" | "end_date">, from: string, to: string) {
  const start = row.start_date > from ? row.start_date : from;
  const end = row.end_date < to ? row.end_date : to;
  if (end < start) return 0;
  const [a, b] = [start, end].map((iso) => { const [y, m, d] = iso.split("-").map(Number); return Date.UTC(y, m - 1, d); });
  return Math.round((b - a) / 86_400_000) + 1;
}
/** Hours a facility was booked (confirmed) between two days. */
export function bookedHours(bookings: Booking[], facility: number, from: string, to: string) {
  return bookings
    .filter((row) => row.facility === facility && row.status === "confirmed")
    .reduce((sum, row) => sum + daysInside(row, from, to) * Math.max(0, minutes(time(row.end_time)) - minutes(time(row.start_time))) / 60, 0);
}

// ---- Bookings ----------------------------------------------------------------------------

export const HOLDING = ["pending", "confirmed"];

export function bookingStatus(row: Pick<Booking, "status" | "start_date" | "end_date">, today: string): Tone {
  if (row.status === "pending") return row.end_date < today ? tone("Never decided", "bg-gray-300") : tone("Awaiting approval", "bg-amber-500");
  if (row.status === "confirmed") {
    if (row.end_date < today) return tone("Held", "bg-gray-300");
    if (row.start_date <= today) return tone("On today", "bg-sky-500");
    return tone("Confirmed", "bg-emerald-500");
  }
  if (row.status === "declined") return tone("Declined", "bg-red-400");
  if (row.status === "completed") return tone("Held", "bg-gray-300");
  return tone("Cancelled", "bg-gray-400");
}

export function bookingActions(row: Booking, me: number | undefined, canApprove: boolean, today: string) {
  const live = HOLDING.includes(row.status) && row.end_date >= today;
  return {
    decide: canApprove && row.status === "pending" && row.end_date >= today,
    cancel: live && (row.booked_by === me || canApprove),
    mine: row.booked_by === me,
  };
}

/** A booking already holding the room on overlapping days and hours, as the server checks it. */
export function findClash(bookings: Booking[], draft: { facility: number; start_date: string; end_date: string; start_time: string; end_time: string }) {
  if (!draft.facility || !draft.start_date || !draft.start_time || !draft.end_time) return undefined;
  const end = draft.end_date || draft.start_date;
  return bookings.find((row) =>
    row.facility === draft.facility && HOLDING.includes(row.status) &&
    row.start_date <= end && row.end_date >= draft.start_date &&
    time(row.start_time) < draft.end_time && time(row.end_time) > draft.start_time);
}

export const bookingWhen = (row: Pick<Booking, "start_date" | "end_date" | "start_time" | "end_time">) =>
  `${weekday(row.start_date)}${row.end_date !== row.start_date ? ` – ${weekday(row.end_date)}` : ""} · ${time(row.start_time)}–${time(row.end_time)}`;

/** Bookings that fall on one day, earliest first. */
export const onDay = (bookings: Booking[], iso: string) =>
  bookings.filter((row) => row.start_date <= iso && row.end_date >= iso && row.status !== "cancelled" && row.status !== "declined").sort((a, b) => a.start_time.localeCompare(b.start_time));

// ---- Maintenance -------------------------------------------------------------------------

export function requestStatus(row: Pick<MaintenanceRequest, "status" | "assigned_to">): Tone {
  if (row.status === "pending") return row.assigned_to ? tone("Assigned", "bg-sky-500") : tone("Waiting", "bg-amber-500");
  return ({ in_progress: tone("Being fixed", "bg-indigo-500"), completed: tone("Fixed", "bg-emerald-500"), cancelled: tone("Cancelled", "bg-gray-400") } as Record<string, Tone>)[row.status];
}

export function requestActions(row: MaintenanceRequest, me: number | undefined, office: boolean) {
  return {
    start: office && row.status === "pending",
    complete: office && row.status === "in_progress",
    cancel: office ? row.status === "pending" || row.status === "in_progress" : row.requested_by === me && row.status === "pending",
  };
}

const priorityRank: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
export const ageInDays = (created: string, today: string) => Math.max(0, Math.round((Date.parse(today) - Date.parse(created.slice(0, 10))) / 86_400_000));

// ---- Registers ----------------------------------------------------------------------------

export const facilityConfig: RegisterConfig<Facility> = {
  search: (row) => `${row.name} ${labelOf(FACILITY_TYPES, row.facility_type)} ${location(row)} ${row.equipment ?? ""}`,
  views: [
    { key: "active", label: "In use", match: (row) => row.is_active },
    { key: "bookable", label: "Booked by request", match: (row) => row.is_active && row.requires_booking },
    { key: "repairs", label: "With open repairs", match: (row) => row.open_requests > 0 },
    { key: "attention", label: "Closed or unsafe", match: (row) => row.is_active && (!row.is_available || row.last_condition === "unsafe" || row.last_condition === "poor") },
    { key: "retired", label: "Out of use", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  category: { label: "Kind", value: (row) => labelOf(FACILITY_TYPES, row.facility_type) },
  sorts: [
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
    { key: "capacity", label: "Largest first", compare: reversed(byNumber((row) => row.capacity ?? 0)) },
    { key: "repairs", label: "Most open repairs", compare: reversed(byNumber((row) => row.open_requests)) },
  ],
};

export function bookingConfig(today: string, me?: number): RegisterConfig<Booking> {
  return {
    search: (row) => `${row.facility_name} ${row.purpose} ${row.booked_by_name} ${row.description ?? ""}`,
    views: [
      { key: "upcoming", label: "Coming up", match: (row) => HOLDING.includes(row.status) && row.end_date >= today },
      { key: "pending", label: "Awaiting approval", match: (row) => row.status === "pending" && row.end_date >= today },
      { key: "today", label: "Today", match: (row) => row.status === "confirmed" && row.start_date <= today && row.end_date >= today },
      { key: "mine", label: "Mine", match: (row) => row.booked_by === me },
      { key: "past", label: "Past", match: (row) => row.end_date < today },
      { key: "closed", label: "Declined or cancelled", match: (row) => row.status === "declined" || row.status === "cancelled" },
      { key: "all", label: "All" },
    ],
    date: { label: "Day", value: (row) => row.start_date },
    category: { label: "Facility", value: (row) => row.facility_name },
    sorts: [
      { key: "soonest", label: "Soonest first", compare: byText((row) => `${row.start_date}${row.start_time}`) },
      { key: "latest", label: "Latest first", compare: reversed(byText((row) => `${row.start_date}${row.start_time}`)) },
      { key: "asked", label: "Asked for first", compare: byText((row) => row.created_at) },
    ],
  };
}

export function requestConfig(me?: number): RegisterConfig<MaintenanceRequest> {
  const open = (row: MaintenanceRequest) => row.status === "pending" || row.status === "in_progress";
  return {
    search: (row) => `${row.facility_name} ${row.title} ${row.description} ${row.requested_by_name} ${row.assigned_to_name}`,
    views: [
      { key: "open", label: "Open", match: open },
      { key: "urgent", label: "Urgent or high", match: (row) => open(row) && (row.priority === "urgent" || row.priority === "high") },
      { key: "waiting", label: "Not started", match: (row) => row.status === "pending" },
      { key: "progress", label: "Being fixed", match: (row) => row.status === "in_progress" },
      { key: "mine", label: "Asked by me", match: (row) => row.requested_by === me },
      { key: "done", label: "Fixed", match: (row) => row.status === "completed" },
      { key: "all", label: "All" },
    ],
    date: { label: "Reported", value: (row) => row.created_at },
    category: { label: "Facility", value: (row) => row.facility_name },
    sorts: [
      { key: "priority", label: "Most urgent first", compare: (a, b) => priorityRank[a.priority] - priorityRank[b.priority] || a.created_at.localeCompare(b.created_at) },
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.created_at)) },
      { key: "oldest", label: "Waiting longest", compare: byText((row) => row.created_at) },
    ],
  };
}

export function recordConfig(today: string): RegisterConfig<MaintenanceRecord> {
  return {
    search: (row) => `${row.facility_name} ${row.title} ${row.description} ${row.performed_by ?? ""} ${row.work_performed ?? ""}`,
    views: [
      { key: "all", label: "All" },
      { key: "due", label: "Next service due", match: (row) => Boolean(row.next_maintenance_date) && row.next_maintenance_date! <= addDays(today, 14) },
      { key: "scheduled", label: "Planned", match: (row) => row.status === "scheduled" || row.status === "in_progress" },
      { key: "done", label: "Done", match: (row) => row.status === "completed" },
    ],
    date: { label: "Done on", value: (row) => row.performed_date },
    category: { label: "Facility", value: (row) => row.facility_name },
    sorts: [
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.performed_date)) },
      { key: "cost", label: "Costliest first", compare: reversed(byNumber((row) => row.total_cost)) },
      { key: "next", label: "Next service soonest", compare: byText((row) => row.next_maintenance_date || "9999") },
    ],
  };
}

export function inspectionConfig(today: string): RegisterConfig<Inspection> {
  const open = (row: Inspection) => row.action_required && !row.follow_up_completed;
  return {
    search: (row) => `${row.facility_name} ${labelOf(INSPECTION_TYPES, row.inspection_type)} ${row.inspector ?? ""} ${row.findings ?? ""} ${row.recommendations ?? ""}`,
    views: [
      { key: "all", label: "All" },
      { key: "action", label: "Action open", match: open },
      { key: "overdue", label: "Follow-up overdue", match: (row) => open(row) && Boolean(row.follow_up_date) && row.follow_up_date! < today },
      { key: "poor", label: "Poor or unsafe", match: (row) => row.overall_condition === "poor" || row.overall_condition === "unsafe" },
    ],
    date: { label: "Inspected", value: (row) => row.inspection_date },
    category: { label: "Kind", value: (row) => labelOf(INSPECTION_TYPES, row.inspection_type) },
    sorts: [
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.inspection_date)) },
      { key: "follow", label: "Follow-up soonest", compare: byText((row) => (open(row) ? row.follow_up_date || "0000" : "9999")) },
    ],
  };
}
